import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Store, ShoppingCart, DollarSign, TrendingUp, FileText } from 'lucide-react';
import { getBackendUrl } from '../../utils/api';

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

export const MasterReportsContent = () => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);

  useEffect(() => {
    fetchReport();
  }, [startDate, endDate]);

  const fetchReport = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/master/reports/financial`, {
        params: { start_date: startDate, end_date: endDate }
      });
      setReport(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching report:', error);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-white">Relatórios Financeiros</h1>
        <p className="text-gray-400 mt-1">Análise consolidada de faturamento</p>
      </div>

      {/* Date Range Selector */}
      <div className="glass-card">
        <div className="flex flex-wrap gap-4 items-end">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-gray-300 mb-2">Data Início</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="glass-input w-full"
            />
          </div>
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-gray-300 mb-2">Data Fim</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="glass-input w-full"
            />
          </div>
          <button onClick={fetchReport} className="glass-button-primary">
            <FileText size={18} className="mr-2" />
            Atualizar
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="glass-card">
          <div className="flex items-center mb-2">
            <DollarSign className="w-5 h-5 text-green-400 mr-2" />
            <span className="text-gray-400 text-sm">Receita Total</span>
          </div>
          <p className="text-3xl font-bold text-white">R$ {report?.period?.total_revenue?.toFixed(2) || '0.00'}</p>
        </div>
        
        <div className="glass-card">
          <div className="flex items-center mb-2">
            <ShoppingCart className="w-5 h-5 text-blue-400 mr-2" />
            <span className="text-gray-400 text-sm">Total de Pedidos</span>
          </div>
          <p className="text-3xl font-bold text-white">{report?.period?.total_orders || 0}</p>
        </div>
        
        <div className="glass-card">
          <div className="flex items-center mb-2">
            <TrendingUp className="w-5 h-5 text-purple-400 mr-2" />
            <span className="text-gray-400 text-sm">Ticket Médio</span>
          </div>
          <p className="text-3xl font-bold text-white">R$ {report?.period?.avg_order_value?.toFixed(2) || '0.00'}</p>
        </div>
        
        <div className="glass-card">
          <div className="flex items-center mb-2">
            <Store className="w-5 h-5 text-orange-400 mr-2" />
            <span className="text-gray-400 text-sm">Lojas Ativas</span>
          </div>
          <p className="text-3xl font-bold text-white">{report?.summary?.active_restaurants || 0}</p>
        </div>
      </div>

      {/* Revenue by Restaurant */}
      <div className="glass-card">
        <h2 className="text-xl font-semibold text-white mb-4">Receita por Loja</h2>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-700">
                <th className="text-left p-3 text-gray-400 font-medium">#</th>
                <th className="text-left p-3 text-gray-400 font-medium">Loja</th>
                <th className="text-right p-3 text-gray-400 font-medium">Pedidos</th>
                <th className="text-right p-3 text-gray-400 font-medium">Receita</th>
                <th className="text-right p-3 text-gray-400 font-medium">Ticket Médio</th>
              </tr>
            </thead>
            <tbody>
              {report?.by_restaurant?.map((item, index) => (
                <tr key={item.restaurant_id} className="border-b border-gray-800 hover:bg-gray-800/30">
                  <td className="p-3 text-gray-400">{index + 1}</td>
                  <td className="p-3 text-white">{item.restaurant_name}</td>
                  <td className="p-3 text-right text-gray-300">{item.orders}</td>
                  <td className="p-3 text-right text-green-400 font-medium">R$ {item.revenue.toFixed(2)}</td>
                  <td className="p-3 text-right text-gray-300">R$ {item.avg_order_value.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};


// ============================================================================
// MASTER TEMPLATES CONTENT (Templates de Configuração)
// ============================================================================

